import React, { useState, useEffect } from "react";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../tools/api_Routes";
import { useNavigate } from "react-router-dom";
import {
  errorToaster,
  successToaster,
} from "../../../helper/toasterConfiguration";
import { Styles } from "./styles";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SubmitOfferCard from "./submit-offer-item";
import PaymentDoc from "../add/payment-doc";

const AddOrderForm = ({
                        panels,
                        isPanelsLoading,
                        users,
                        isUsersLoading,
                        offers,
                        isOffersLoading,
                      }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState();
  const [uploadOption, setUploadOption] = useState("logo");
  const [price, setPrice] = useState();
  const [payment_note, setPayment_note] = useState("LOREM");
  const itemsPerPage = 25;

  // Add state for selected user and filtered panels
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFilteredPanels, setUserFilteredPanels] = useState([]);

  // Panels State
  const [panelsPage, setPanelsPage] = useState(1);
  const [panelSearchQuery, setPanelSearchQuery] = useState("");

  // Filter panels based on selected user's ID
  useEffect(() => {
    if (selectedUser && panels.length > 0) {
      // Filter panels that have user_id equal to the selected user's ID
      const filtered = panels.filter(panel => panel.user_id === selectedUser.id);
      setUserFilteredPanels(filtered);

      // Reset panel selection when user changes
      setData(prev => ({ ...prev, panel_id: "" }));
    } else {
      // If no user is selected, show all panels
      setUserFilteredPanels(panels);
    }
  }, [selectedUser, panels]);

  // Use userFilteredPanels for display
  const paginatedPanels = userFilteredPanels.slice(0, panelsPage * itemsPerPage);
  const filteredPanels = userFilteredPanels.filter((panel) =>
      panel.name.toLowerCase().includes(panelSearchQuery.toLowerCase())
  );
  const displayedPanels = panelSearchQuery ? filteredPanels : paginatedPanels;

  // Users State
  const [usersPage, setUsersPage] = useState(1);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const paginatedUsers = users.slice(0, usersPage * itemsPerPage);
  const filteredUsers = users.filter((user) =>
      user.name.toLowerCase().includes(userSearchQuery.toLowerCase())
  );
  const displayedUsers = userSearchQuery ? filteredUsers : paginatedUsers;

  // Offers State
  const [offersPage, setOffersPage] = useState(1);
  const [offerSearchQuery, setOfferSearchQuery] = useState("");
  const paginatedOffers = offers.slice(0, offersPage * itemsPerPage);
  const filteredOffers = offers.filter((offer) =>
      offer.title.toLowerCase().includes(offerSearchQuery.toLowerCase())
  );
  const displayedOffers = offerSearchQuery ? filteredOffers : paginatedOffers;

  const [data, setData] = useState({
    offer_id: "",
    panel_id: "",
    price: "",
    payment_method: "",
    user_id: "",
    payment_document: "",
    payment_note: "",
    discount_type: "none",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setPrice(null);
  };

  const handlePanelChange = (event, value) => {
    setData((prev) => ({ ...prev, panel_id: value?.id || "" }));
  };

  const handleUserChange = (event, value) => {
    setSelectedUser(value);
    setData((prev) => ({
      ...prev,
      user_id: value?.id || "",
      panel_id: "" // Reset panel selection when user changes
    }));
  };

  const handleOfferChange = (event, value) => {
    setData((prev) => ({ ...prev, offer_id: value?.offer_id || "" }));
    setSelectedOffer(value);
  };

  const handleChange = (index, field, value) => {
    if (index || index == 0) {
      setData((prev) => ({
        ...prev,
        [`${field}_${index}`]: value,
      }));
    } else {
      setData((prev) => ({
        ...prev,
        [`${field}`]: value,
      }));
    }
    console.log(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = ["offer_id", "panel_id"];

    const updatedFormData = {
      ...data,
    };

    const missingFields = requiredFields.filter(
        (field) => !updatedFormData[field]
    );
    // Validate customLogo properties for each offer detail with a picture
    selectedOffer?.offerDetails?.forEach((item, idx) => {
      if (item?.picture) {
        if (
            !data?.[`logo_${idx + 1}`] &&
            !data?.[`customlogoName_${idx + 1}`]
        ) {
          missingFields.push(`customlogoName for detail ${idx + 1}`);
        }
      }
    });

    if (missingFields.length > 0) {
      errorToaster(
          `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const form_data = new FormData();

    form_data.append("payment_document", data.payment_document);
    // Construct requests array for offer details
    const offer_details_data = [];
    selectedOffer?.offerDetails?.forEach((item, idx) => {
      const request = {};
      if (item.title || item.description || item.picture || item.priority)
        request.offer_detail_id = String(item?.id) || "";
      if (item.title) request.title = data[`title_${idx + 1}`] || "";
      if (item.description)
        request.description = data[`description_${idx + 1}`] || "";
      if (item.priority) {
        request.priority = data[`priority_${idx + 1}`] || "";
      }
      if (item.picture) {
        if (uploadOption === "customLogo") {
          request.customLogo = {
            fontSize: data[`fontSize_${idx + 1}`]
                ? `${data[`fontSize_${idx + 1}`]}px`
                : "30px",
            color: data[`selectedColor_${idx + 1}`] || "#FFFFFF",
            fontFamily: data[`selectedFont_${idx + 1}`] || "Mockads",
            logoName: data[`customlogoName_${idx + 1}`],
          };
        } else {
          request.logo = data[`logo_${idx + 1}`];
        }
      }
      offer_details_data.push(request);
    });
    updatedFormData.payment_note = payment_note;
    Object.keys(updatedFormData).forEach((key) => {
      if (
          !key.startsWith("title_") &&
          !key.startsWith("description_") &&
          !key.startsWith("priority_") &&
          !key.startsWith("logoName_") &&
          !key.startsWith("selectedColor_") &&
          !key.startsWith("selectedFont_") &&
          !key.startsWith("fontSize_")
      ) {
        if (key === "user_id") {
          if (updatedFormData[key]) form_data.append(key, updatedFormData[key]);
        } else {
          if (key === "offer_id") {
            form_data.append(key, String(updatedFormData[key]));
          } else {
            form_data.append(key, updatedFormData[key]);
          }
        }
      }
    });

    // Append offer_details_data array to form_data
    offer_details_data.forEach((request, index) => {
      Object.keys(request).forEach((key) => {
        if (key === "customLogo") {
          Object.keys(request[key]).forEach((logoKey) => {
            form_data.append(
                `offer_details_data[${index}][customLogo][${logoKey}]`,
                request[key][logoKey]
            );
          });
        } else {
          form_data.append(
              `offer_details_data[${index}][${key}]`,
              request[key]
          );
        }
      });
    });
    const result = await Helper.Post({
      url: api_Routes.Orders.AddOfferOrder,
      data: form_data,
      hasToken: true,
    });

    const { response, message } = result || {};

    if (response) {
      successToaster(message);
      navigate("/Orders");
    }
    setIsLoading(false);
  };

  return (
      <Styles>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-6">
          <div className="flex flex-col justify-between items-center gap-6 md:flex-row">
            <div className="flex flex-col justify-between items-center gap-6 md:flex-row w-full">
              {/* Users Autocomplete */}
              <div className="w-full">
                <Autocomplete
                    disablePortal
                    loading={isUsersLoading}
                    options={displayedUsers}
                    getOptionLabel={(option) => option.name || ""}
                    onChange={handleUserChange}
                    onInputChange={(event, value) => setUserSearchQuery(value)}
                    renderInput={(params) => (
                        <TextField {...params} label="User" variant="outlined" />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        if (
                            listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 3
                        ) {
                          setUsersPage((prevPage) => prevPage + 1);
                        }
                      },
                    }}
                />
              </div>

              {/* Panels Autocomplete */}
              <div className="w-full">
                <Autocomplete
                    disablePortal
                    loading={isPanelsLoading}
                    options={displayedPanels}
                    getOptionLabel={(option) => option.name || ""}
                    onChange={handlePanelChange}
                    onInputChange={(event, value) => setPanelSearchQuery(value)}
                    value={displayedPanels.find(panel => panel.id === data.panel_id) || null}
                    disabled={!data.user_id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Panel"
                            variant="outlined"
                            helperText={!data.user_id ? "Please select a user first" : ""}
                        />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        if (
                            listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 3
                        ) {
                          setPanelsPage((prevPage) => prevPage + 1);
                        }
                      },
                    }}
                />
              </div>
            </div>
          </div>
          {/* Offers Autocomplete */}
          <div className="w-full">
            <Autocomplete
                disablePortal
                loading={isOffersLoading}
                options={displayedOffers}
                getOptionLabel={(option) => option.title || ""}
                onChange={handleOfferChange}
                onInputChange={(event, value) => setOfferSearchQuery(value)}
                renderInput={(params) => (
                    <TextField {...params} label="Offer" variant="outlined" />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                        listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 3
                    ) {
                      setOffersPage((prevPage) => prevPage + 1);
                    }
                  },
                }}
            />
          </div>

          {selectedOffer && (
              <Box sx={{ marginTop: "40px" }}>
                <Box>
                  {selectedOffer?.offerDetails?.map((detail, index) => {
                    return (
                        <SubmitOfferCard
                            key={index}
                            index={index}
                            detail={detail}
                            formData={data}
                            setData={setData}
                            handleChange={handleChange}
                            uploadOption={uploadOption}
                            setUploadOption={setUploadOption}
                        />
                    );
                  })}
                </Box>
              </Box>
          )}
          <div className="flex flex-col justify-between items-center gap-x-6 md:flex-row">
            {selectedOffer && (
                <>
                  {" "}
                  <TextField
                      fullWidth
                      label="Price"
                      name="price"
                      type={"number"}
                      required
                      autoComplete="off"
                      value={data?.price || price || ""}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: Boolean(data?.price || price),
                      }}
                      sx={{ mb: 2 }}
                  />
                  <TextField
                      fullWidth
                      label="Discount"
                      name="discount"
                      type={"number"}
                      autoComplete="off"
                      value={data?.discount || ""}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{
                        shrink: Boolean(data?.discount),
                      }}
                      sx={{ mb: 2 }}
                  />
                  <FormControl className="w-full">
                    <InputLabel id="discount-type-select-label">
                      Discount Type
                    </InputLabel>
                    <Select
                        labelId="discount-type-select-label"
                        id="discount-type-select"
                        value={data.discount_type || "none"}
                        onChange={(e) => {
                          setData((prev) => ({
                            ...prev,
                            ["discount_type"]: e.target.value,
                          }));
                        }}
                        label="Discount Type"
                        sx={{ textAlign: "start", mb: 2 }}
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="const">Constant</MenuItem>
                      <MenuItem value="percentage">Percentage</MenuItem>
                    </Select>
                  </FormControl>
                </>
            )}
          </div>
          <div className="submit-btn">
            <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isLoading}
            >
              {isLoading ? (
                  <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                  "Submit"
              )}
            </Button>
          </div>
        </form>
      </Styles>
  );
};

export default AddOrderForm;